import type { Event } from "./types";
import { addCalendarDays, isUpcomingConcertEvent } from "@finda/shared/genres";
import { groupRecurringEvents } from "./utils";
import { getEventFeedItems } from "./event-feed";

export const GENRE_PAGE_SIZE = 24;
export const GENRE_CALENDAR_CACHE_TAG = "genre-calendars";
export const GENRE_DATES = ["all", "today", "tonight", "weekend", "week", "month"] as const;
export const GENRE_VIEWS = ["list", "grid", "map"] as const;
export type GenreDate = (typeof GENRE_DATES)[number];
export interface GenreBrowseOptions { page: number; date: GenreDate; view: (typeof GENRE_VIEWS)[number] }
export type GenreSearchParams = Record<string, string | string[] | undefined>;

export function parseGenreBrowseOptions(params: GenreSearchParams): GenreBrowseOptions | null {
  const { page = "1", date = "all", view = "list" } = params;
  if (typeof page !== "string" || !/^[1-9]\d*$/.test(page) || !Number.isSafeInteger(Number(page))) return null;
  if (typeof date !== "string" || !GENRE_DATES.includes(date as GenreDate)) return null;
  if (typeof view !== "string" || !GENRE_VIEWS.includes(view as GenreBrowseOptions["view"])) return null;
  return { page: Number(page), date: date as GenreDate, view: view as GenreBrowseOptions["view"] };
}

export function genreBrowseHref(base: string, options: Partial<GenreBrowseOptions> = {}): string {
  const query = new URLSearchParams();
  if (options.date && options.date !== "all") query.set("date", options.date);
  if (options.view && options.view !== "list") query.set("view", options.view);
  if (options.page && options.page !== 1) query.set("page", String(options.page));
  return query.size ? `${base}?${query}` : base;
}

export function genreCalendarSeo(base: string, options: GenreBrowseOptions) {
  return {
    canonical: base,
    indexableVariant: options.page === 1 && options.date === "all" && options.view === "list",
  };
}

export function filterGenreCalendar(events: Event[], options: GenreBrowseOptions, today: string, now = new Date()): Event[] {
  const dayOfWeek = new Date(`${today}T12:00:00Z`).getUTCDay();
  const friday = addCalendarDays(today, dayOfWeek === 0 ? -2 : dayOfWeek <= 5 ? 5 - dayOfWeek : -1);
  const sunday = addCalendarDays(friday, 2);
  const weekEnd = addCalendarDays(today, dayOfWeek === 0 ? 0 : 7 - dayOfWeek);
  return events.filter((event) => {
    const date = event.local_date;
    if (!date || !isUpcomingConcertEvent(event, today, now)) return false;
    if (options.date === "today" && date !== today) return false;
    if (options.date === "tonight" && (date !== today || !event.local_time || Number(event.local_time.slice(0, 2)) < 17)) return false;
    if (options.date === "weekend" && (date < friday || date > sunday)) return false;
    if (options.date === "week" && date > weekEnd) return false;
    if (options.date === "month" && date.slice(0, 7) !== today.slice(0, 7)) return false;
    return true;
  });
}

export function paginateGenreCalendar(events: Event[], page: number) {
  const groups = getEventFeedItems(groupRecurringEvents(events), [], "list").flatMap((item) => item.type === "event" ? [item.group] : []);
  const pageCount = Math.max(1, Math.ceil(groups.length / GENRE_PAGE_SIZE));
  if (page > pageCount) return null;
  const pageGroups = groups.slice((page - 1) * GENRE_PAGE_SIZE, page * GENRE_PAGE_SIZE);
  return { groups: pageGroups, events: pageGroups.flatMap((group) => group.events), billCount: groups.length, eventCount: events.length, pageCount };
}
